import React, { useMemo, useState } from 'react';
import { normalizeInput } from '../utils/actionTrackingUtils';

const inputClass = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

const QUICK_ADD = [
  { name: 'Count', unit: '', kind: 'COUNT' },
  { name: 'Steps', unit: 'steps', kind: 'NUMBER' },
  { name: 'Water', unit: 'ml', kind: 'NUMBER' },
  { name: 'Calories', unit: 'kcal', kind: 'NUMBER' },
  { name: 'Sales', unit: '', kind: 'NUMBER' },
  { name: 'Calls', unit: '', kind: 'COUNT' },
  { name: 'Distance', unit: 'km', kind: 'NUMBER' },
  { name: 'Sleep', unit: 'hours', kind: 'NUMBER' },
  { name: 'Weight', unit: 'kg', kind: 'NUMBER' },
];

const INPUTS = [
  { id: 'TEXTBOX', label: 'Textbox' },
  { id: 'DROPDOWN', label: 'Dropdown' },
  { id: 'RADIO', label: 'Radio' },
  { id: 'CHECKBOX', label: 'Checkbox' },
];

const emptyEntityDraft = (kind, name = '', unit = '') => ({
  name,
  unit,
  kind,
  input: 'TEXTBOX',
  options: [],
  optionInput: '',
});

const ActionTrackingSetup = ({
  form,
  onChange,
  errors = {},
  users = [],
  currentUser,
}) => {
  const config = form.configuration || {};
  const trackKind = String(config.trackKind || '').toUpperCase();
  const entities = Array.isArray(config.entities) ? config.entities : [];
  const share = Boolean(config.actionShare)
    || (form.participants?.required || []).length > 1;
  const [entityOpen, setEntityOpen] = useState(false);
  const [draft, setDraft] = useState(emptyEntityDraft('NUMBER'));
  const [quickName, setQuickName] = useState('');
  const [quickUnit, setQuickUnit] = useState('');

  const people = useMemo(() => {
    const byId = new Map();
    users.forEach((user) => {
      if (user?.id) byId.set(String(user.id), user);
    });
    if (currentUser?.id) byId.set(String(currentUser.id), { ...currentUser, ...(byId.get(String(currentUser.id)) || {}) });
    return Array.from(byId.values());
  }, [users, currentUser]);

  const patchConfig = (patch, extra = {}) => {
    onChange({
      configuration: { ...config, ...patch },
      ...extra,
    });
  };

  const setKind = (kind, extras = {}) => {
    const creatorId = currentUser?.id;
    const required = share
      ? (form.participants?.required || [])
      : (creatorId ? [{ userId: creatorId, role: 'REQUIRED', status: 'ACCEPTED' }] : []);
    patchConfig(
      { trackKind: kind, ...extras },
      {
        category: share ? 'SOCIAL' : 'PERSONAL',
        responsePolicy: { ...form.responsePolicy, type: 'NONE' },
        participants: {
          ...form.participants,
          mode: share ? 'SELECT' : 'SELF',
          allowInviteOthers: true,
          required,
          optional: [],
        },
      },
    );
  };

  const openEntity = () => {
    setDraft(emptyEntityDraft(
      trackKind === 'COUNT' ? 'COUNT' : 'NUMBER',
      entities.length === 0 ? quickName : '',
      entities.length === 0 ? quickUnit : '',
    ));
    setEntityOpen(true);
  };

  const saveEntity = () => {
    const name = String(draft.name || '').trim();
    if (!name) return;
    const input = normalizeInput(draft.input);
    const needsOptions = input !== 'TEXTBOX';
    const options = (draft.options || []).map((item) => String(item).trim()).filter(Boolean);
    if (needsOptions && options.length < 2) return;
    const next = [
      ...entities,
      {
        id: `en-${Date.now()}`,
        name,
        unit: trackKind === 'COUNT' ? '' : String(draft.unit || '').trim(),
        kind: trackKind === 'COUNT' ? 'COUNT' : 'NUMBER',
        input,
        options,
      },
    ];
    patchConfig({ entities: next });
    setQuickName('');
    setQuickUnit('');
    setEntityOpen(false);
  };

  const removeEntity = (id) => {
    patchConfig({ entities: entities.filter((item) => item.id !== id) });
  };

  const setShare = (nextShare) => {
    const creatorId = currentUser?.id;
    patchConfig(
      { actionShare: nextShare },
      {
        category: nextShare ? 'SOCIAL' : 'PERSONAL',
        participants: {
          ...form.participants,
          mode: nextShare ? 'SELECT' : 'SELF',
          allowInviteOthers: true,
          required: nextShare
            ? (form.participants?.required?.length
              ? form.participants.required
              : (creatorId ? [{ userId: creatorId, role: 'REQUIRED', status: 'ACCEPTED' }] : []))
            : (creatorId ? [{ userId: creatorId, role: 'REQUIRED', status: 'ACCEPTED' }] : []),
          optional: [],
        },
      },
    );
  };

  const togglePerson = (user) => {
    if (String(user.id) === String(currentUser?.id)) return;
    const required = form.participants?.required || [];
    const exists = required.some((item) => String(item.userId) === String(user.id));
    const next = exists
      ? required.filter((item) => String(item.userId) !== String(user.id))
      : [...required, { userId: user.id, role: 'REQUIRED', status: 'INVITED', userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() }];
    onChange({
      participants: { ...form.participants, mode: 'SELECT', required: next, optional: [] },
      configuration: { ...config, actionShare: true },
    });
  };

  const selectedIds = new Set((form.participants?.required || []).map((item) => String(item.userId)));

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-bold text-gray-900 dark:text-white">How do you wanna track?</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Pick Number or Count first. Entities are optional — skip them for a simple counter.
        </p>
        {errors.trackKind && <p className="text-xs text-rose-500 mt-1">{errors.trackKind}</p>}
        <div className="grid grid-cols-2 gap-2 mt-3">
          {[
            { id: 'NUMBER', title: 'Number', subtitle: 'Log values' },
            { id: 'COUNT', title: 'Count', subtitle: 'Tap +1' },
          ].map((item) => {
            const selected = trackKind === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setKind(item.id)}
                className={`text-left p-3 rounded-xl border-2 ${
                  selected
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <p className="text-sm font-bold text-gray-900 dark:text-white">{item.title}</p>
                <p className="text-xs text-gray-500">{item.subtitle}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-sm font-bold text-gray-900 dark:text-white">Quick add</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {QUICK_ADD.map((item) => (
            <button
              key={item.name}
              type="button"
              onClick={() => {
                setQuickName(item.name);
                setQuickUnit(item.unit);
                setKind(item.kind);
              }}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 dark:border-gray-700 hover:border-primary-400"
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>

      {trackKind ? (
        <div className="space-y-3">
          <div>
            <p className="text-sm font-bold text-gray-900 dark:text-white">
              {entities.length ? 'Add another entity' : 'Entity 1 (optional)'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              You can skip this and just use a simple {trackKind === 'COUNT' ? '+1 counter' : 'number log'}.
            </p>
          </div>
          {entities.map((entity, index) => (
            <div
              key={entity.id || index}
              className="flex items-center justify-between gap-3 rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-950/20 px-3 py-2"
            >
              <div>
                <p className="text-sm font-bold text-gray-900 dark:text-white">{entity.name}</p>
                <p className="text-xs text-teal-700 dark:text-teal-300">
                  {entity.kind === 'COUNT' ? 'Count' : 'Number'} · {entity.input || 'TEXTBOX'}
                  {entity.options?.length ? ` · ${entity.options.join(', ')}` : ''}
                </p>
              </div>
              <button type="button" onClick={() => removeEntity(entity.id)} className="text-gray-400 hover:text-rose-500">×</button>
            </div>
          ))}
          <button
            type="button"
            onClick={openEntity}
            className="w-full rounded-xl py-3 text-sm font-bold text-white bg-gradient-to-r from-sky-500 to-blue-600"
          >
            {entities.length ? `+ Add entity ${entities.length + 1}` : '+ Add entity 1'}
          </button>
        </div>
      ) : null}

      <div>
        <p className="text-sm font-bold text-gray-900 dark:text-white">Share</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Keep it for yourself, or share so others can track live with you.
        </p>
        {errors.participants && <p className="text-xs text-rose-500 mt-1">{errors.participants}</p>}
        <div className="grid grid-cols-2 gap-2 mt-3 p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
          <button
            type="button"
            onClick={() => setShare(false)}
            className={`rounded-lg py-2 text-sm font-bold ${!share ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300'}`}
          >
            For self
          </button>
          <button
            type="button"
            onClick={() => setShare(true)}
            className={`rounded-lg py-2 text-sm font-bold ${share ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300'}`}
          >
            Share with others
          </button>
        </div>
        {share && (
          <div className="mt-3 max-h-48 overflow-y-auto space-y-1">
            {people.map((user) => {
              const locked = String(user.id) === String(currentUser?.id);
              const selected = selectedIds.has(String(user.id));
              const label = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'User';
              return (
                <label key={user.id} className="flex items-center gap-2 text-sm py-1">
                  <input
                    type="checkbox"
                    checked={selected}
                    disabled={locked}
                    onChange={() => togglePerson(user)}
                  />
                  <span className="text-gray-800 dark:text-gray-200">{label}{locked ? ' (you)' : ''}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {entityOpen && (
        <div className="fixed inset-0 z-[1300] flex items-end sm:items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 p-4 space-y-3">
            <p className="text-lg font-bold text-gray-900 dark:text-white">Entity {entities.length + 1}</p>
            <label className={labelClass}>
              Entity name
              <input
                value={draft.name}
                onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                className={inputClass}
                placeholder={trackKind === 'COUNT' ? 'e.g. Glass of water' : 'e.g. Push-ups'}
              />
            </label>
            {trackKind !== 'COUNT' && (
              <label className={labelClass}>
                Unit (optional)
                <input
                  value={draft.unit}
                  onChange={(e) => setDraft((prev) => ({ ...prev, unit: e.target.value }))}
                  className={inputClass}
                  placeholder="ml, kg, km…"
                />
              </label>
            )}
            <p className="text-sm font-bold text-gray-900 dark:text-white">How should people fill this?</p>
            <div className="flex flex-wrap gap-2">
              {INPUTS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, input: item.id }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                    draft.input === item.id
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {draft.input !== 'TEXTBOX' && (
              <div>
                <div className="flex gap-2">
                  <input
                    value={draft.optionInput}
                    onChange={(e) => setDraft((prev) => ({ ...prev, optionInput: e.target.value }))}
                    className={inputClass}
                    placeholder="Add option"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = String(draft.optionInput || '').trim();
                        if (!value) return;
                        setDraft((prev) => ({
                          ...prev,
                          options: [...(prev.options || []), value],
                          optionInput: '',
                        }));
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="px-3 rounded-lg bg-primary-600 text-white font-bold"
                    onClick={() => {
                      const value = String(draft.optionInput || '').trim();
                      if (!value) return;
                      setDraft((prev) => ({
                        ...prev,
                        options: [...(prev.options || []), value],
                        optionInput: '',
                      }));
                    }}
                  >
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(draft.options || []).map((option) => (
                    <span key={option} className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-xs">
                      {option}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEntityOpen(false)} className="px-3 py-2 text-sm">Cancel</button>
              <button type="button" onClick={saveEntity} className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-bold">
                Add entity
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionTrackingSetup;
